import { ApolloCache, gql } from '@apollo/client'

import { IAddonServiceItem, IAddonServiceForm, IAddonServiceUpdate } from 'features/Ancillaries/types'
import { EConfirmationModality } from 'features/Bookings/types'
import {
  GTServiceFeeAddonRule,
  GTServiceFeeAddonRuleCompany,
  GTServiceFeeAddonRuleCompanyLine,
  GTServiceFeeAddonRuleSalesChannel
} from 'gql/types.generated'
import { RecursivePartial } from 'types/index'
import { sanitizePrice } from 'utils/math'

export const TICKET_FEES_LIST = [1, 2, 3, 4, 5, 6, 8, 10]

export const mapAddonServiceQueryToForm = (
  addonService: RecursivePartial<GTServiceFeeAddonRule>
): IAddonServiceForm => {
  const company = (addonService.company as GTServiceFeeAddonRuleCompany) || { id: '', name: '' }
  const companyLine = (addonService.companyLine as GTServiceFeeAddonRuleCompanyLine) || { id: '', name: '' }
  const salesChannels = (addonService.salesChannelsV2 as GTServiceFeeAddonRuleSalesChannel[]) || []
  const confirmationModalities = addonService.confirmationModalities
    ? (addonService.confirmationModalities as string[]).map<EConfirmationModality>((confirmationModality): TSFixMe => {
        return confirmationModality.toLowerCase() as EConfirmationModality
      })
    : []
  const segments = addonService.segments
    ? addonService.segments.map(
        (segment: TSFixMe): TSFixMe => ({
          ...segment,
          code: `${segment?.origin?.code}-${segment?.destination?.code}`
        })
      )
    : []
  const ticketFees = addonService.ticketFees
    ? addonService.ticketFees.map((ticketFee: TSFixMe): TSFixMe => {
        const { quantity, fees } = ticketFee
        const newFees = fees.map(
          ({ amount, currencyCode }: TSFixMe): TSFixMe => ({
            amount: (amount as number) / 100,
            currencyCode
          })
        )
        return { quantity, fees: newFees }
      })
    : TICKET_FEES_LIST.map(
        (ticket): TSFixMe => ({
          quantity: ticket,
          fees: []
        })
      )

  return {
    company,
    companyLine,
    salesChannels,
    confirmationModalities,
    segments,
    ticketFees
  }
}

export const mapAddonServiceFormToQuery = (addonService: IAddonServiceForm): IAddonServiceUpdate => ({
  companyID: addonService.company.id,
  companyLineID: addonService.companyLine.id,
  salesChannelIDs: addonService.salesChannels.map((salesChannel) => salesChannel.id),
  confirmationModalities: addonService.confirmationModalities.map(
    (confirmationModality): TSFixMe => confirmationModality.toUpperCase() as Uppercase<EConfirmationModality>
  ),
  segmentIDs: addonService.segments.map((segment): TSFixMe => segment.id),
  ticketFees: addonService.ticketFees.map(({ quantity, fees }): TSFixMe => {
    return {
      quantity,
      fees: fees.map(
        ({ amount, currencyCode }): TSFixMe => ({
          currencyCode,
          amount: sanitizePrice((amount as number) * 100, true)
        })
      )
    }
  })
})

export const resolvers = {
  Mutation: {
    activateAddonService: (
      _root: unknown,
      { addonService }: { addonService: IAddonServiceItem },
      { cache }: { cache: ApolloCache<IAddonServiceItem> }
    ): null => {
      cache.writeFragment({
        id: cache.identify({
          __typename: 'ServiceFeeAddonRule',
          id: addonService.id
        }),
        fragment: gql`
          fragment ServiceFeeAddonRuleEnabled on ServiceFeeAddonRule {
            isEnabled
          }
        `,
        data: {
          __typename: 'ServiceFeeAddonRule',
          isEnabled: true
        }
      })

      return null
    },
    deactivateAddonService: (
      _root: unknown,
      { addonService }: { addonService: IAddonServiceItem },
      { cache }: { cache: ApolloCache<IAddonServiceItem> }
    ): null => {
      cache.writeFragment({
        id: cache.identify({
          __typename: 'ServiceFeeAddonRule',
          id: addonService.id
        }),
        fragment: gql`
          fragment ServiceFeeAddonRuleEnabled on ServiceFeeAddonRule {
            isEnabled
          }
        `,
        data: {
          __typename: 'ServiceFeeAddonRule',
          isEnabled: false
        }
      })

      return null
    }
  }
}
